import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "bg-gray-400 text-white px-1 py-0.5 rounded-md font-mono"
}

import { Handle, Position } from "@vue-flow/core";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import OaiTooltip from "shared/components/other/OaiTooltip.vue";
import { useHasPermission } from "shared/composables/project";
import {
  CriticalPathLayout,
  CriticalPathContext,
  CriticalPathNodeEx,
  CriticalPathEx,
  CriticalPathDelta,
} from "shared/types/CriticalPath";
import CriticalPathGraphNodeDelay from "shared/views/critical_path/components/CriticalPathGraphNodeDelay.vue";
import CriticalPathGraphNodeHover from "shared/views/critical_path/components/CriticalPathGraphNodeHover.vue";
import { useCriticalPathTagLetterById } from "../composables";


export default /*#__PURE__*/_defineComponent({
  __name: 'CriticalPathGraphNode',
  props: {
    criticalPath: {},
    criticalPathNode: {},
    context: {},
    layout: {},
    dimmed: { type: Boolean },
    selected: { type: Boolean },
    highlightedYellow: { type: Boolean },
    highlightedBlue: { type: Boolean },
    highlightedPurple: { type: Boolean },
    deltaForNode: {},
    isLastNode: { type: Boolean }
  },
  setup(__props: any) {

const props = __props;

const { t } = useI18n();

const isAdmin = useHasPermission(["app_admin", "pct_admin"]);

const criticalPathTagLetterById = useCriticalPathTagLetterById(computed(() => props.criticalPath));

const backgroundColor = computed(() => {
  if (props.selected) {
    const selectedStateClasses: Record<CriticalPathNodeEx["state"], string> = {
      not_started: "bg-gray-200",
      in_progress: "bg-yellow-600 text-white",
      finished: "bg-green-400 text-white",
    };
    return selectedStateClasses[props.criticalPathNode.state];
  }

  const classes: Record<CriticalPathNodeEx["state"], string> = {
    not_started: "bg-white",
    in_progress: "bg-yellow-400 text-white",
    finished: "bg-green-200 text-white",
  };
  return classes[props.criticalPathNode.state];
});

const tagLetters = computed(() => {
  const letters = props.criticalPathNode.tags.map(
    (tagId) => criticalPathTagLetterById.value[tagId],
  );
  letters.sort();
  return letters.join(",");
});

const highlighted = computed(
  () => props.highlightedPurple || props.highlightedBlue || props.highlightedYellow,
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["h-full w-full rounded-xl flex items-center justify-center px-1 text-xs relative border border-gray-600", [backgroundColor.value, _ctx.selected ? 'shadow-lg' : 'shadow-md']]),
    style: _normalizeStyle({ filter: _ctx.dimmed && !_ctx.selected && !highlighted.value ? 'opacity(0.3)' : '' })
  }, [
    (_ctx.highlightedYellow || _ctx.highlightedBlue || _ctx.highlightedPurple)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(["absolute -top-[20px] -left-[17px] -right-[17px] -bottom-[20px] -z-[10] rounded-lg border flex justify-center items-end text-xs", 
        _ctx.highlightedYellow
          ? 'bg-yellow-100 border-yellow-400 text-yellow-800'
          : _ctx.highlightedBlue
          ? 'bg-blue-200 border-blue-400 text-blue-800'
          : 'bg-purple-100 border-purple-400 text-purple-800'
      ]),
          style: { fontSize: '8px' }
        }, _toDisplayString(_ctx.highlightedYellow
          ? _unref(t)("analytics.critical_path.time_critical_component_label")
          : _ctx.highlightedBlue
          ? _unref(t)("analytics.critical_path.latest_planned_component_label")
          : _unref(t)("analytics.critical_path.projected_end_component_label")), 3))
      : _createCommentVNode("", true),
    (_ctx.criticalPathNode.level_id !== _ctx.layout.levels[0]?.id)
      ? (_openBlock(), _createBlock(_unref(Handle), {
          key: 1,
          type: "source",
          position: _unref(Position).Top
        }, null, 8, ["position"]))
      : _createCommentVNode("", true),
    _createVNode(OaiTooltip, {
      cls: "truncate",
      simple: false
    }, {
      tooltip: _withCtx(() => [
        _createVNode(CriticalPathGraphNodeHover, {
          criticalPath: _ctx.criticalPath,
          criticalPathNode: _ctx.criticalPathNode,
          context: _ctx.context,
          deltaForNode: _ctx.deltaForNode
        }, null, 8, ["criticalPath", "criticalPathNode", "context", "deltaForNode"])
      ]),
      default: _withCtx(() => [
        (tagLetters.value && _unref(isAdmin))
          ? (_openBlock(), _createElementBlock("span", _hoisted_1, _toDisplayString(tagLetters.value), 1))
          : _createCommentVNode("", true),
        _createTextVNode(" " + _toDisplayString(_ctx.criticalPathNode.name) + " ", 1)
      ]),
      _: 1
    }),
    (_ctx.criticalPathNode.level_id !== _ctx.layout.levels[_ctx.layout.levels.length - 1]?.id)
      ? (_openBlock(), _createBlock(_unref(Handle), {
          key: 2,
          type: "target",
          position: _unref(Position).Bottom
        }, null, 8, ["position"]))
      : _createCommentVNode("", true),
    (_ctx.criticalPathNode.level_splits.length > 0)
      ? (_openBlock(), _createBlock(OaiTooltip, {
          key: 3,
          cls: "absolute bottom-0 right-0 translate-x-1/2 translate-y-1/2 bg-gray-400 px-2 text-xs py-0.5 rounded-full text-white"
        }, {
          tooltip: _withCtx(() => [
            _createElementVNode("div", null, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.criticalPathNode.level_splits, (split) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: split.id
                }, " • " + _toDisplayString(split.name), 1))
              }), 128))
            ])
          ]),
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.criticalPathNode.level_splits.length) + " ", 1)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _createVNode(CriticalPathGraphNodeDelay, {
      criticalPathNode: _ctx.criticalPathNode,
      context: _ctx.context,
      selected: _ctx.selected,
      deltaForNode: _ctx.deltaForNode,
      isLastNode: _ctx.isLastNode
    }, null, 8, ["criticalPathNode", "context", "selected", "deltaForNode", "isLastNode"])
  ], 6))
}
}

})